import type { TenantKeyDomain } from "../shared/credentials/crypto";
import {
  type HpkePublicKey,
  hpkeSeal,
  parsePublicKey,
} from "../shared/credentials/hpkeFormat";
import { credentialPublicKeyPath } from "./lib/apiPaths";
import { apiUrl } from "./lib/tenantOrigin";

const cachedKeys = new Map<TenantKeyDomain, Promise<HpkePublicKey | null>>();

// The tenant's sealing key for a domain, fetched from the API (which resolves
// the tenant from the Host header and mints the key on first request).
// Fetched once per page load per domain; a failed fetch clears the cache so
// the next call retries. A 404 means the stack has no credential keys
// configured (fresh installs), which callers handle by storing plaintext —
// any other failure throws rather than silently downgrading a secret to
// plaintext.
function getPublicKey(domain: TenantKeyDomain): Promise<HpkePublicKey | null> {
  let cached = cachedKeys.get(domain);
  if (!cached) {
    cached = (async () => {
      const res = await fetch(`${apiUrl()}${credentialPublicKeyPath(domain)}`, {
        credentials: "omit",
      });
      if (res.status === 404) return null;
      if (!res.ok) {
        throw new Error(`credential public key fetch failed: ${res.status}`);
      }
      const body = (await res.json()) as { public_key: string };
      return parsePublicKey(body.public_key);
    })().catch((err) => {
      cachedKeys.delete(domain);
      throw err;
    });
    cachedKeys.set(domain, cached);
  }
  return cached;
}

/**
 * Encrypt a secret value with HPKE for storage. Returns the $hpke$ envelope.
 * If the stack has no credential keys configured, returns the plaintext
 * unchanged.
 */
export async function encryptSecret(
  plaintext: string,
  domain: TenantKeyDomain = "credentials"
): Promise<string> {
  const publicKey = await getPublicKey(domain);
  if (!publicKey) return plaintext;
  return hpkeSeal(plaintext, publicKey);
}
